import { useState } from "react";
import { Button } from "@/src/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/src/components/ui/dialog";
import { useFetch } from "@/src/lib/useFetch";
import { useToast } from "@/src/components/ui/use-toast";
import { Combobox } from "@/src/components/ui/combobox";
import { extractFromMultiName } from "../utils";
import { useNavigate } from "react-router-dom";

type Value = {
  id: string;
  names: string;
};
export type MergeLocationsButtonProps = {
  source: string;
};

export function MergeLocationsButton({ source }: MergeLocationsButtonProps) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<Value | null>(null);
  const { fetch } = useFetch();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleMerge = async () => {
    try {
      await fetch("/api/locations/locations", "PATCH", {
        change: "merge",
        source,
        target: target?.id,
      });

      toast({
        title: "Success",
        description: "Locations merged successfully",
      });

      setOpen(false);
      navigate(`/locations/${target!.id}`);
    } catch (error: any) {
      console.error("Error merging locations:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };
  return (
    <>
      <Button className="ml-2" type="button" onClick={() => setOpen(true)} variant="secondary">
        Merge
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Merge Locations</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Target Location</label>
              <Combobox<Value>
                className="w-full"
                loadOptions={async (query) => {
                  if (!query) return [];
                  const response = await fetch<{ data: Value[] }>(
                    `/api/locations/locations?filter=${encodeURIComponent(query)}`,
                  );
                  return response.data.filter((r) => r.id !== source);
                }}
                value={target}
                renderOption={(v) => extractFromMultiName(v.names)}
                onChange={(v) => setTarget(v)}
                placeholder="Search for location"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleMerge} disabled={!target}>
              Merge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
